import './styles.scss'

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Form } from 'antd'

import B2becInfoLayout from '@/components/B2becInfoLayout/B2becInfoLayout'
import B2becSpinner from '@/components/B2becSpinner'
import { B2becStepItemLabel, B2becSteps } from '@/components/B2becSteps'
import { NotificationManager } from '@/components/Notification'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectCartData, selectFetchCartState } from '@/store/selectors/cartSelector'
import { getIsLoadingCheckout } from '@/store/selectors/checkoutSelector'
import { selectDeactivateShoppingCart, selectRegions } from '@/store/selectors/configSelector'
import { trackCart } from '@/store/slices/adobeAnalyticSlice'
import {
  selectCustomerAddress,
  selectDeliveryAddresses,
  selectIsFetchingCustomerAddress,
} from '@/store/slices/customerSlice'
import { ordersActions } from '@/store/slices/orderSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { CheckoutProvider } from '@/contexts/checkout/CheckoutProvider'
import { ASYNC_STATUS } from '@/libs/constants'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { getAddress } from '@/libs/utils/addressUtils'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import StepOneComponent from './StepOneComponent'
import StepThreeComponent from './StepThreeComponent'
import StepTwoComponent from './StepTwoComponent'

const step1ValidateFields = [
  'newAddrName',
  'newAddrName2',
  'newAddrStreet',
  'newAddrHouseNumber',
  'newAddrCity',
  'newAddrCountry',
  'newAddrZip',
  'newAddrRegion',
  'newAddrPhoneNumber',
  'newAddrEmail',
]

const step2ValidationFields = ['referenceNumber', 'shippingNotes']
const step3ValidationFields = ['shippingNotesStep3']

function getFormDeliveryAddr(value) {
  if (value.currentDeliveryAddr === 2) {
    return {
      name: value.newAddrName,
      name2: value.newAddrName2,
      street: value.newAddrStreet,
      street4: value.newAddrStreet4,
      houseNumber: value.newAddrHouseNumber,
      city: value.newAddrCity,
      country: value.myAddrCountry,
      region: value.newAddrRegion,
      zip: value.newAddrZip,
      phoneNumber: value.newAddrPhoneNumber,
      email: value.newAddrEmail,
      isNewDeliveryAddress: true,
    }
  }

  return {
    name: value.myAddrName,
    name2: value.myAddrName2,
    street: value.myAddrStreet,
    street4: value.myAddrStreet4,
    houseNumber: value.myAddrHouseNumber,
    city: value.myAddrCity,
    country: value.myAddrCountry,
    region: value.myAddrRegion,
    zip: value.myAddrZip,
    phoneNumber: value.myAddrPhoneNumber,
    email: value.myAddrEmail,
    isNewDeliveryAddress: false,
  }
}

function CheckoutPage({ breadcrumbs }) {
  useDocumentTitle('pageTitle.checkoutCart')
  useBreadcrumbs(breadcrumbs)

  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [currentTab, setCurrentTab] = useState(1)
  const customerAddress = useAppSelector(selectCustomerAddress)
  const deliveryAddresses = useAppSelector(selectDeliveryAddresses)
  const listCart = useAppSelector(selectCartData)
  const fetchCartState = useAppSelector(selectFetchCartState)
  const isCustomerLoading = useAppSelector(selectIsFetchingCustomerAddress)
  const isCheckoutLoading = useAppSelector(getIsLoadingCheckout)
  const regions = useAppSelector(selectRegions)
  const isShoppingCartDeactivated = useAppSelector(
    selectDeactivateShoppingCart,
  )

  const { setPageInfoData } = useAdobeAnalysis(TRACK_DATA.CART)

  useEffect(() => {
    if (
      (fetchCartState !== ASYNC_STATUS.IDLE && listCart?.items?.length <= 0)
      || isShoppingCartDeactivated
    ) {
      NotificationManager.error({
        message: 'notification.error.proceedCheckout',
        description: 'notification.error.cartListIsEmpty',
      })

      navigate(linkGenerator('/cart'), { replace: true })
    }
    if ((listCart?.items || []).some((item) => !!item?.error)) {
      NotificationManager.error({
        message: 'notification.error.proceedCheckout',
        description: 'notification.error.nonexistentNumberInCart',
      })

      navigate(linkGenerator('/cart'), { replace: true })
    }
  }, [listCart, navigate, isShoppingCartDeactivated, fetchCartState])

  useEffect(() => {
    setPageInfoData({
      pageName: `checkout step ${currentTab}`,
      pageId: '3',
      pageType: PAGE_TYPES.CHECKOUT,
    })
  }, [currentTab, setPageInfoData])

  useEffect(() => {
    if (
      fetchCartState === ASYNC_STATUS.SUCCESS
      && isValidArray(listCart?.items)
    ) {
      dispatch(
        trackCart({
          [TRACK_DATA.CART]: listCart?.items,
        }),
      )
    }
  }, [dispatch, fetchCartState, listCart])

  const handleValidateFields = (tabIndex) => {
    let validateFields = []

    if (currentTab.toString() === '3') {
      validateFields = [...step3ValidationFields]
    } else if (tabIndex === '2') {
      validateFields = [...step1ValidateFields]
    } else if (tabIndex === '3') {
      validateFields = [...step1ValidateFields, ...step2ValidationFields]
    }

    return validateFields
  }

  const changeTabHandler = async (tabIndex) => {
    const tabName = tabIndex.toString()
    const validateFields = handleValidateFields(tabName)
    try {
      await form.validateFields(validateFields)
      window.scrollTo({ top: 0 })

      setCurrentTab(+tabIndex)
    } catch (errorInfo) {
      NotificationManager.error({
        message: (errorInfo?.errorFields || []).map((error) => (
          <span key={error?.errors} style={{ display: 'block' }}>
            {error?.errors}
          </span>
        )),
      })
    }
  }

  const handleSubmit = (value) => {
    if (!value) {
      return
    }
    const deliveryAddr = getFormDeliveryAddr(value)

    if (deliveryAddr.region && regions[deliveryAddr.region]) {
      deliveryAddr.regionCode = deliveryAddr.region
      deliveryAddr.region = regions[deliveryAddr.region].label
    }

    const submitValue = {
      referenceNumber: value.referenceNumber,
      deliveryAddressPartnerNumber: value.currentDeliveryAddr === 1
        ? value.myAddrPartnerNumber
        : value.newAddrPartnerNumber,
      deliveryAddress: deliveryAddr,
      shippingNotes: value.shippingNotes,
      ...(value?.serviceCode && { serviceCode: value?.serviceCode }),
    }

    const payload = {
      ...submitValue,
      navigate,
    }

    dispatch(ordersActions.placeOrder(payload))
  }

  const initialValues = useMemo(() => {
    const address = isValidArray(deliveryAddresses)
      ? deliveryAddresses[0]
      : customerAddress
    const names = getAddress(address).name

    return {
      myAddrName: names,
      myAddrName2: '',
      myAddrCity: address?.city,
      myAddrCountry: address.country,
      myAddrHouseNumber: address?.houseNumber,
      myAddrStreet: address?.street,
      myAddrStreet4: address?.street4,
      myAddrRegion: address?.region,
      myAddrZip: address?.zip,
      myAddrPhoneNumber: address?.phoneNumber,
      myAddrEmail: address?.eMail,
      myAddrPartnerNumber: address?.partnerNumber,
      currentDeliveryAddr: 1,
    }
  }, [deliveryAddresses, customerAddress])

  if (isCustomerLoading) {
    return <B2becSpinner isLoading />
  }

  const items = [
    {
      key: '1',
      label: (
        <B2becStepItemLabel
          index={1}
          label={t('checkout.tab.addressInformation')}
        />
      ),
      children: (
        <StepOneComponent
          form={form}
          setTabHandler={changeTabHandler}
          tab={currentTab}
        />
      ),
    },
    {
      key: '2',
      label: (
        <B2becStepItemLabel
          index={2}
          label={t('checkout.tab.paymentAndShipping')}
        />
      ),
      children: (
        <StepTwoComponent
          form={form}
          setTabHandler={changeTabHandler}
          tab={currentTab}
        />
      ),
    },
    {
      key: '3',
      label: (
        <B2becStepItemLabel
          index={3}
          label={t('checkout.tab.reviewAndOrder')}
        />
      ),
      children: (
        <StepThreeComponent
          form={form}
          setTabHandler={changeTabHandler}
          submitFormHandler={() => form.submit()}
          tab={currentTab}
        />
      ),
    },
  ]

  return (
    <CheckoutProvider>
      <B2becSpinner isLoading={isCheckoutLoading} />
      <Form
        form={form}
        initialValues={initialValues}
        labelAlign="left"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        validateMessages={{
          string: {
            max: t('errors.maxInputExceeded'),
          },
        }}
        onFinish={handleSubmit}
      >
        <div className="checkout">
          <B2becInfoLayout>
            <B2becInfoLayout.Title>
              <h1 className="checkout__title">{t('checkout.headline')}</h1>
            </B2becInfoLayout.Title>
            <B2becInfoLayout.Content>
              <B2becSteps
                activeKey={`${currentTab}`}
                defaultActiveKey="1"
                items={items}
                onChange={async (key) => changeTabHandler(key)}
              />
            </B2becInfoLayout.Content>
          </B2becInfoLayout>
        </div>
      </Form>
    </CheckoutProvider>
  )
}

export default CheckoutPage
